import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupStickySelector();
    this.#scheduleStickySwatchSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  handleAddToCartClick = async () => {
    const variantId = Number(this.dataset.currentVariantId);
    if (!variantId) return;

    this.refs.addToCartButton.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add sticky variant to cart');
      }

      this.refs.addToCartButton.dataset.added = 'true';
      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      this.refs.addToCartButton.removeAttribute('data-added');
    } finally {
      this.refs.addToCartButton.disabled = false;
    }
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    this.#setupStickySelector();
    this.#scheduleStickySwatchSync();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = () => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  #setupStickySelector() {
    const trigger = this.querySelector('[data-sticky-selector-trigger]');
    const menu = this.querySelector('[data-sticky-selector-menu]');
    const label = this.querySelector('[data-sticky-selector-label]');
    const triggerSwatch = this.querySelector('[data-sticky-selector-swatch]');
    const options = this.querySelectorAll('[data-sticky-option-value]');

    if (!trigger || !menu || !label || !triggerSwatch || options.length === 0) return;

    trigger.onclick = (event) => {
      event.stopPropagation();

      const isOpen = !menu.hasAttribute('hidden');

      if (isOpen) {
        menu.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        menu.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
      }
    };

    options.forEach((option) => {
      option.onclick = () => {
        const value = option.getAttribute('data-sticky-option-value');
        const variantId = option.getAttribute('data-sticky-variant-id');
        const variantAvailable = option.getAttribute('data-sticky-variant-available') === 'true';
        const variantTitle = option.getAttribute('data-sticky-variant-title');
        const variantPrice = option.getAttribute('data-sticky-variant-price');
        const variantComparePrice = option.getAttribute('data-sticky-variant-compare-price');
        const variantImage = option.getAttribute('data-sticky-variant-image');
        const variantImageAlt = option.getAttribute('data-sticky-variant-image-alt');
        const optionSwatch = option.querySelector('[data-sticky-option-swatch]');

        if (!value || !variantId) return;

        this.dataset.currentVariantId = variantId;
        this.dataset.variantAvailable = variantAvailable ? 'true' : 'false';
        label.textContent = value;

        if (triggerSwatch instanceof HTMLElement && optionSwatch instanceof HTMLElement) {
          triggerSwatch.innerHTML = optionSwatch.innerHTML;
        }

        this.#applyStickyVariantData({
          value,
          variantTitle,
          variantPrice,
          variantComparePrice,
          variantImage,
          variantImageAlt,
          variantAvailable,
        });

        options.forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-selected', 'false');
        });

        option.classList.add('is-selected');
        option.setAttribute('aria-selected', 'true');

        this.refs.addToCartButton.disabled = !variantAvailable;

        menu.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
      };
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Node)) return;

        if (!this.contains(event.target)) {
          menu.setAttribute('hidden', '');
          trigger.setAttribute('aria-expanded', 'false');
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  #applyStickyVariantData({
    value,
    variantTitle,
    variantPrice,
    variantComparePrice,
    variantImage,
    variantImageAlt,
    variantAvailable,
  }) {
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const priceElement = this.querySelector('[data-testid="sticky-price-display"]');
    const productImage = this.refs.productImage;

    if (variantTitleElement) {
      variantTitleElement.textContent = variantTitle || value;
    }

    if (priceElement) {
      if (variantComparePrice) {
        priceElement.innerHTML = `
          <span class="price-item price-item--sale">${variantPrice}</span>
          <s class="compare-at-price">${variantComparePrice}</s>
        `;
      } else {
        priceElement.innerHTML = `
          <span class="price-item price-item--regular">${variantPrice}</span>
        `;
      }
    }

    if (productImage instanceof HTMLImageElement && variantImage) {
      productImage.src = variantImage;
      productImage.alt = variantImageAlt || '';
    }

    this.refs.addToCartButton.disabled = !variantAvailable;
  }

  #getSourceSwatchElements() {
    const avp = Array.from(
      document.querySelectorAll('label.avp-productoptionswatchwrapper.apo-swatch-wrapper')
    );

    if (avp.length) {
      return avp
        .map((option) => option.querySelector('.ap-options__swatch, .avp-productoptionswatch'))
        .filter((el) => el instanceof HTMLElement);
    }

    const kaching = Array.from(
      document.querySelectorAll('.kaching-bundles__swatch-dropdown.kaching-bundles-option')
    );

    if (kaching.length) {
      return kaching
        .map((option) => option.querySelector('.kaching-bundles__swatch'))
        .filter((el) => el instanceof HTMLElement);
    }

    return [];
  }

  #syncAllStickyOptionSwatches() {
    const stickyOptions = Array.from(this.querySelectorAll('[data-sticky-option-value]'));
    const sourceSwatches = this.#getSourceSwatchElements();

    stickyOptions.forEach((option, index) => {
      const swatchHolder = option.querySelector('[data-sticky-option-swatch]');
      const sourceSwatch = sourceSwatches[index];

      if (!(swatchHolder instanceof HTMLElement) || !(sourceSwatch instanceof HTMLElement)) return;

      swatchHolder.innerHTML = sourceSwatch.outerHTML;
    });
  }

  #syncInitialStickyTriggerSwatch() {
    const triggerSwatch = this.querySelector('[data-sticky-selector-swatch]');
    const selectedOption = this.querySelector('[data-sticky-option-value].is-selected');
    const selectedOptionSwatch = selectedOption?.querySelector('[data-sticky-option-swatch]');

    if (!(triggerSwatch instanceof HTMLElement) || !(selectedOptionSwatch instanceof HTMLElement)) return;

    triggerSwatch.innerHTML = selectedOptionSwatch.innerHTML;
  }

  #scheduleStickySwatchSync() {
    let attempts = 0;

    const run = () => {
      this.#syncAllStickyOptionSwatches();
      this.#syncInitialStickyTriggerSwatch();

      const triggerSwatch = this.querySelector('[data-sticky-selector-swatch]');
      const hasVisualSwatch =
        triggerSwatch instanceof HTMLElement &&
        !!triggerSwatch.querySelector(
          '.ap-options__swatch, .avp-productoptionswatch, .kaching-bundles__swatch, img'
        );

      if (hasVisualSwatch || attempts >= 10) return;

      attempts += 1;
      setTimeout(run, 150);
    };

    run();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}